<template>
  <div class="userGuide main-content">
    <div class="page-header">
      <div class="d-flex flex-row">
        <div>
          <h3 class="page-title">{{ $tc("user_guides.user_guide", 2) }}</h3>
        </div>
      </div>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}</router-link
          >
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">{{
            $tc("user_guides.user_guide", 2)
          }}</router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div class="col-xs-2 mr-4">
          <base-button
            v-show="totalUserGuides || filtersApplied"
            :outline="true"
            :icon="filterIcon"
            color="theme"
            size="large"
            right-icon
            @click="toggleFilter"
          >
            {{ $t("general.filter") }}
          </base-button>
        </div>
        <div slot="item-title" class="col-xs-2">
          <base-button
            color="theme"
            icon="plus"
            size="large"
            @click="addUserGuide"
          >
            {{ $t("user_guides.add_user_guide") }}
          </base-button>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-show="showFilters" class="filter-section">
        <div class="row">
          <div class="col-sm-4">
            <label class="form-label"> {{ $tc("user_guides.name") }} </label>
            <base-input
              v-model="filters.name"
              type="text"
              name="company_name"
              autocomplete="off"
            />
          </div>
          <div class="col-sm-4">
            <label class="form-label"> {{ $tc("user_guides.type") }} </label>
            <base-select
              v-model="filters.type"
              :options="TypeOptions"
              :allow-empty="false"
              :show-labels="false"
              :tabindex="5"
              :placeholder="$t('user_guides.select_type')"
              label="name"
              track-by="name"
            />
          </div>
          <div class="col-sm-4">
            <label class="form-label"> {{ $tc("user_guides.status") }} </label>
            <base-select
              v-model="filters.status"
              :options="statusOptions"
              :allow-empty="false"
              :show-labels="false"
              :tabindex="5"
              :placeholder="$t('user_guides.select_status')"
              label="name"
              track-by="name"
            />
          </div>
          <label class="clear-filter" @click="clearFilter">
            {{ $t("general.clear_all") }}</label
          >
        </div>
      </div>
    </transition>
    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <satellite-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{ $t("user_guides.no_user_guides") }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("user_guides.list_of_user_guides")
        }}</label>
      </div>
      <div class="btn-container">
        <base-button
          :outline="true"
          color="theme"
          class="mt-3"
          size="large"
          @click="addUserGuide"
        >
          {{ $t("user_guides.add_new_user_guide") }}
        </base-button>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ userGuides.length }}</b>
          {{ $t("general.of") }} <b>{{ totalUserGuides }}</b>
        </p>
        <transition name="fade">
          <v-dropdown v-if="selectedUserGuides.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <div class="dropdown-item" @click="removeMultipleUserGuides">
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="dropdown-item-icon"
                />
                {{ $t("general.delete") }}
              </div>
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>
      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllUserGuides"
        />
        <label
          v-show="!isRequestOngoing"
          for="select-all"
          class="custom-control-label selectall"
        >
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>
      <table-component
        ref="userGudiesTable"
        :data="fetchData"
        :show-filter="false"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column
          :sortable="true"
          :label="$t('user_guides.name')"
          show="name"
        />
        <table-column
          :sortable="true"
          :label="$t('user_guides.type')"
          show="type"
        />
        <table-column
          :sortable="true"
          :label="$t('user_guides.status')"
          show="status"
        />
        <table-column
          :label="$t('user_guides.added_on')"
          sort-as="created_at"
          show="formattedCreatedAt"
        />
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span>{{ $t("user_guides.action") }}</span>
            <v-dropdown>
              <a slot="activator" href="#"><dot-icon /></a>
              <v-dropdown-item>
                <router-link
                  :to="{ path: `user-guides/${row.id}/view` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                  {{ $t("general.view") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item>
                <div
                  class="dropdown-item"
                  @click="UserGuidestatusChange(row.unique_hash)"
                >
                  <font-awesome-icon
                    icon="arrows-alt-h"
                    class="dropdown-item-icon"
                  />
                  {{ $t("user_guides.status_change") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item>
                <div class="dropdown-item" @click="removeUserGuide(row.id)">
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.delete") }}
                </div>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DotIcon from "../../components/icon/DotIcon";
import SatelliteIcon from "../../components/icon/SatelliteIcon";
import BaseButton from "../../../js/components/base/BaseButton";

export default {
  components: {
    DotIcon,
    SatelliteIcon,
    BaseButton
  },
  data() {
    return {
      id: null,
      showFilters: false,
      sortedBy: "created_at",
      isRequestOngoing: true,
      filtersApplied: false,
      TypeOptions: [
        { name: "Supplier" },
        { name: "Anchor" },
        { name: "Funder" }
      ],
      statusOptions: [{ name: "Active" }, { name: "Deactive" }],
      filters: {
        name: null,
        type: null,
        status: null
      }
    };
  },
  computed: {
    ...mapGetters("userGuide", [
      "userGuides",
      "selectedUserGuides",
      "totalUserGuides",
      "selectAllField"
    ]),
    showEmptyScreen() {
      return (
        !this.totalUserGuides && !this.isRequestOngoing && !this.filtersApplied
      );
    },
    filterIcon() {
      return this.showFilters ? "times" : "filter";
    },
    selectField: {
      get: function() {
        return this.selectedUserGuides;
      },
      set: function(val) {
        this.selectUserGuide(val);
      }
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {
    filters: {
      handler: "setFilters",
      deep: true
    }
  },

  destroyed() {
    if (this.selectAllField) {
      this.selectAllUserGuides();
    }
  },
  methods: {
    ...mapActions("userGuide", [
      "fetchUserGuides",
      "selectAllUserGuides",
      "selectUserGuide",
      "deleteUserGuide",
      "deleteMultipleUserGuides",
      "setSelectAllState",
      "statusChange"
    ]),
    ...mapActions("modal", ["openModal", "closeModal"]),
    refreshTable() {
      this.$refs.userGudiesTable.refresh();
    },
    async fetchData({ page, per_page, filter, sort }) {
      let data = {
        name: this.filters.name !== null ? this.filters.name : "",
        type: this.filters.type !== null ? this.filters.type.name : "",
        status: this.filters.status !== null ? this.filters.status.name : "",
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "asc",
        per_page, // Add rowsPerPage in the API request
        page
      };

      this.isRequestOngoing = true;
      let response = await this.fetchUserGuides(data);
      this.isRequestOngoing = false;

      return {
        data: response.data.userGuides.data,
        pagination: {
          totalPages: response.data.userGuides.last_page,
          currentPage: page,
          rowsPerPage: response.data.userGuides.per_page // Add rowsPerPage in the API request
        }
      };
    },
    setFilters() {
      this.filtersApplied = true;
      this.refreshTable();
    },
    clearFilter() {
      this.filters = {
        type: "",
        status: ""
      };

      this.$nextTick(() => {
        this.filtersApplied = false;
      });
    },
    toggleFilter() {
      if (this.showFilters && this.filtersApplied) {
        this.clearFilter();
        this.refreshTable();
      }

      this.showFilters = !this.showFilters;
    },
    async removeMultipleUserGuides() {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("user_guides.confirm_delete", 2),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deleteMultipleUserGuides();
          if (res.data.success || res.data.userGuide) {
            window.toastr["success"](
              this.$tc("user_guides.deleted_message", 2)
            );
            this.refreshTable();
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    addUserGuide() {
      this.openModal({
        title: "Add User Guide",
        componentName: "UserGuideModal"
      });
      this.refreshTable();
    },
    viewUserGuide(data) {
      return window.open(`/user_guide/pdf/${data}`, "_blank");
    },
    removeUserGuide(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("user_guides.confirm_delete", 1),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async value => {
        if (value) {
          let response = await this.deleteUserGuide(id);
          if (response.data.success) {
            window.toastr["success"](
              this.$tc("user_guides.deleted_message", 1)
            );
            this.id = null;
            this.refreshTable();
            return true;
          }
        }
      });
    },
    editUserGuide(data) {
      this.openModal({
        title: "Edit User Guide",
        componentName: "UserGuideModal",
        id: data.id,
        data: data
      });
      this.refreshTable();
    },
    async UserGuidestatusChange(id) {
      let response = await this.statusChange(id);
      if (response.data.success) {
        window.toastr["success"](this.$t("user_guides.status_change_message"));
      }
      this.refreshTable();
    }
  }
};
</script>
